import {
  JE_ParsingContext,
  createFQClaimXform, JE_FQClaimXformSpec,
  JE_FQClaimSpec, JE_FQContentDefinitionSpec,
  createFQClaim,
  createFQDisplayControl,
  createFQTechnicalProfile,
  fetchDefaultFQContentDefinitionRef,
  getOrCreateFQContentDefinition,
  getOrCreateSpecialClaim
} from '../../../../jsonParsing/jsonFQContext';
import constants from '../../../../constants/jsonXMLConstants';
import { extractSettingFieldValue } from '../../../../jsonParsing/utils';
import jsonConstants from '../../../../constants/jsonConstants';

interface SharedIds {
  secretId: string,
  otpCodeId: string,
  verifyInstructions: string,
  beginVerifyId: string,
  verifyOtpId: string
}

const BEGIN_VERIFY_DISPLAY_NAME = "Begin Verify TOTP"
const VERIFY_DISPLAY_NAME = "Verify TOTP"
const TOTP_CODE_NAME = "TOTP CODE"
const SECRET_CLAIM_NAME = "Authenticator Secret"
const SAVE_SECRET_ID = "Saves authenticator secret to AAD"
const READ_SECRET_ID = "Reads authenticator secret to AAD"

const SECRET_CLAIM_INFO = {
  id: "extension_totpSecretKey",
  dataType: "string",
  displayName: SECRET_CLAIM_NAME,
  userInputType: "TextBox"
}

const TOTP_CLAIM_INFO = {
  id: "totpCode",
  dataType: "string",
  displayName: TOTP_CODE_NAME,
  userInputType: "TextBox"
}

const INSTRUCTION_CLAIM_INFO = {
  id: "QrCodeVerifyInstructions",
  dataType: "string",
  displayName: "Enter the verification code from your authenticator app.",
  userInputType: "Paragraph"
}

const findClaim = (
  parsingCtx: JE_ParsingContext,
  claimName: string
) => Object.values(parsingCtx.claimStore.claims)
  .find(claim => claim.displayName === claimName)

const createOrGetClaim = (
  parsingCtx: JE_ParsingContext,
  claimInfo: JE_FQClaimSpec
): string => {
  const claimName = claimInfo.displayName;
  const preExistingClaim = findClaim(
    parsingCtx, claimName
  );

  if (preExistingClaim) {
    return preExistingClaim.id
  }
  return createFQClaim(
    parsingCtx.claimStore,
    claimInfo
  ).id
}

const createVerifyOtp = (parsingCtx: JE_ParsingContext, otpCodeId: string) => {
  const validateOTP = createFQTechnicalProfile(
    parsingCtx.technicalProfileStore, {
    id: "MFA-TOTP-Verify-Code",
    displayName: VERIFY_DISPLAY_NAME,
    protocol: constants.customPolicySchema.protocols.azureMfa,
    metadata: [{ key: "Operation", value: "VerifyOTP" }],
    inputClaims: [{ claimId: otpCodeId, partnerClaimType: "otpCode" }]
  }, constants.claimsProviderNames.totp
  )
  return validateOTP.id;
}

const createBeginVerify = (
  parsingCtx: JE_ParsingContext,
  secretClaimId: string,
  upnClaimId: string,
  objectIdClaim: string
) => {
  const beginVerify = createFQTechnicalProfile(
    parsingCtx.technicalProfileStore, {
    id: "Begin-Verify-TOTP",
    displayName: BEGIN_VERIFY_DISPLAY_NAME,
    protocol: constants.customPolicySchema.protocols.azureMfa,
    metadata: [{ key: "Operation", value: "BeginVerifyOTP" }],
    inputClaims: [{
      claimId: objectIdClaim,
    }, {
      claimId: upnClaimId,
      partnerClaimType: constants.specialAttributes.userPrincipalName
    }, {
      claimId: secretClaimId,
      partnerClaimType: "secretKey"
    }]
  }, constants.claimsProviderNames.totp);
  return beginVerify.id;
}

export function createOrGetVerifyProfiles(
  parsingCtx: JE_ParsingContext,
  objectIdClaimId: string,
  upnClaimId: string
): SharedIds {
  const ids = {
    secretId: getOrCreateSpecialClaim(parsingCtx, constants.specialClaims.totpSecretKey.id),
    otpCodeId: getOrCreateSpecialClaim(parsingCtx, constants.specialClaims.totpCode.id),
    verifyInstructions: getOrCreateSpecialClaim(parsingCtx, constants.specialClaims.QrCodeVerifyInstructions.id),
    beginVerifyId: "",
    verifyOtpId: "",
  };

  const tpStore = parsingCtx.technicalProfileStore.claimsProviders[
    constants.claimsProviderNames.totp
  ];
  const f = (displayName: string) => Object.values(
    tpStore.technicalProfiles).find(tp => tp.displayName === displayName);
  if (!f(VERIFY_DISPLAY_NAME)) {
    ids.verifyOtpId = createVerifyOtp(parsingCtx, ids.otpCodeId)
  } else {
    ids.verifyOtpId = f(VERIFY_DISPLAY_NAME)?.id!
  }
  // dont share these TP's because they could have different upnClaims
  ids.beginVerifyId = createBeginVerify(parsingCtx, ids.secretId, upnClaimId, objectIdClaimId);
  return ids
}

export function createVerifyTOTPSelfAsserted(
  parsingCtx: JE_ParsingContext,
  sanitizedDisplayName: string,
  ids: SharedIds,
  idClaim: string | undefined,
  contentDefinitionUrl: string | null | undefined
) {
  if (!idClaim) {
    throw new Error("Id claim must be set for an authenticator verification step");
  }
  let contentDefinition = fetchDefaultFQContentDefinitionRef(
    parsingCtx, "api.selfasserted"
  )
  if (contentDefinitionUrl) {
    const draftCD: JE_FQContentDefinitionSpec = {
      id: `authenticator-mfa-${parsingCtx.contentDefinitionStore.cdIds.size}`,
      loadUri: contentDefinitionUrl,
      recoveryUri: '~/common/default_page_error.html',
      dataUri: 'urn:com:microsoft:aad:b2c:elements:contract:selfasserted:2.1.9',
      metadata: [{ key: 'DisplayName', value: 'Multi-Factor Authentication' }],
    };
    contentDefinition = getOrCreateFQContentDefinition(
      parsingCtx, draftCD, "api.selfasserted.mfa"
    ).id
  }
  const MFA_TOTP = createFQTechnicalProfile(
    parsingCtx.technicalProfileStore, {
      id: sanitizedDisplayName,
      displayName: "EnableTOTPAuth",
      protocol: constants.customPolicySchema.protocols.selfAsserted,
      metadata: [{
        key: "ContentDefinitionReferenceId", value: contentDefinition,
      }, {
        key: "language.button_continue", value: "Continue"
      }],
      cryptographicKeys: [{ id: "issuer_secret", storageReferenceId: "B2C_1A_TokenSigningKeyContainer" }],
      displayClaims: [{
        claimId: ids.otpCodeId
      }, {
        claimId: ids.verifyInstructions
      }],
      outputClaims: [{
        claimId: idClaim,
      }, {
        claimId: ids.otpCodeId
      }],
      validationTechnicalProfiles: [{ profileId: ids.verifyOtpId }]
    },
    constants.claimsProviderNames.totp
  );
  return MFA_TOTP.id
}
